/**
 * Schreibwege der Trainingsplaene (F-34 A): Bibliothek, Plaene mit
 * Versionen, Einheiten mit Uebungen, Kopieren und Import.
 */
import { db } from '@/db/schema';
import { inDenPapierkorb } from '@/db/repositories/papierkorb';
import { orderZwischen } from '@/domain/listen/listen';
import { STANDARD_UEBUNGEN } from '@/domain/training/bibliothek';
import { standardSaetze, wochenMontag } from '@/domain/training/plan';
import type { ImportErgebnis } from '@/domain/training/import';
import type {
  EinheitTyp, PlanEinheit, PlanUebung, TagArt, TrainingsEinstellungen, TrainingsPlan, Uebung, Wochentag,
} from '@/domain/training/typen';
import { formatDate, nowInstant, today } from '@/lib/datetime';
import { newId } from '@/lib/utils/id';
import type { ID, LocalDate } from '@/domain/types';
import type { MuskelId } from '@/domain/training/muskeln';

// --- Bibliothek ----------------------------------------------------------------

/**
 * Mitgelieferte Uebungen anlegen, die noch fehlen - eigene Aenderungen
 * bleiben. In einer Transaktion: Mehrere Seiten rufen das gleichzeitig auf.
 */
export function bibliothekSicherstellen(): Promise<number> {
  return db.transaction('rw', db.uebungen, async () => {
    const da = new Set(await db.uebungen.toCollection().primaryKeys());
    const ts = nowInstant();
    const neu: Uebung[] = STANDARD_UEBUNGEN.filter((s) => !da.has(s.id)).map((s) => ({
      id: s.id, name: s.name, typ: s.typ, hauptmuskeln: [...s.haupt], hilfsmuskeln: [...s.hilf], standard: { ...s.standard },
      alternativen: [...(s.alternativen ?? [])], standardUebung: true, createdAt: ts, updatedAt: ts,
      ...(s.koerpergewicht ? { koerpergewicht: true } : {}), ...(s.stufen ? { stufen: [...s.stufen] } : {}), ...(s.hinweis ? { hinweis: s.hinweis } : {}),
    }));
    if (neu.length) await db.uebungen.bulkAdd(neu);
    return neu.length;
  });
}

export async function uebungAnlegen(name: string, typ: EinheitTyp = 'kraft'): Promise<Uebung> {
  const ts = nowInstant();
  const u: Uebung = {
    id: newId(), name: name.trim() || 'Neue Übung', typ, hauptmuskeln: [], hilfsmuskeln: [],
    standard: typ === 'mobility' ? { saetze: 2, dauerSek: 45, pauseSek: 0 } : { saetze: 3, wdh: 8, wdhBis: 12, pauseSek: 90 },
    alternativen: [], createdAt: ts, updatedAt: ts,
  };
  await db.uebungen.add(u);
  return u;
}

export type UebungsAenderung = Partial<Pick<Uebung, 'name' | 'typ' | 'hauptmuskeln' | 'hilfsmuskeln' | 'standard' | 'alternativen' | 'hinweis' | 'koerpergewicht' | 'stufen' | 'archiviert'>>;

export async function uebungAendern(id: ID, patch: UebungsAenderung): Promise<void> {
  const p = { ...patch };
  // Ein Muskel ist Haupt- ODER Hilfsmuskel.
  if (p.hauptmuskeln) p.hauptmuskeln = [...new Set(p.hauptmuskeln)];
  if (p.hilfsmuskeln) p.hilfsmuskeln = [...new Set(p.hilfsmuskeln)].filter((m) => !(p.hauptmuskeln ?? []).includes(m));
  if (p.alternativen) p.alternativen = [...new Set(p.alternativen)].filter((a) => a !== id);
  await db.uebungen.update(id, { ...p, updatedAt: nowInstant() });
}

// --- Plaene --------------------------------------------------------------------

const RUHE: Record<Wochentag, TagArt> = { 1: 'ruhe', 2: 'ruhe', 3: 'ruhe', 4: 'ruhe', 5: 'ruhe', 6: 'ruhe', 7: 'ruhe' };

export async function planAnlegen(name: string, gueltigAb: LocalDate = today()): Promise<TrainingsPlan> {
  const ts = nowInstant();
  const id = newId();
  const ersterAktiver = !(await db.trainingsplaene.filter((p) => p.aktiv).count());
  const plan: TrainingsPlan = {
    id, familie: id, name: name.trim() || 'Trainingsplan', version: 1, gueltigAb, aktiv: ersterAktiver,
    tage: { ...RUHE }, wochen: 1, createdAt: ts, updatedAt: ts,
  };
  await db.trainingsplaene.add(plan);
  return plan;
}

export type PlanAenderung = Partial<Pick<TrainingsPlan, 'name' | 'tage' | 'wochen' | 'zyklusStart' | 'aufwaermen' | 'notiz'>>;

export async function planAendern(id: ID, patch: PlanAenderung): Promise<void> {
  const p = { ...patch };
  if (p.wochen !== undefined) p.wochen = Math.max(1, Math.min(12, Math.round(p.wochen)));
  if (p.zyklusStart) p.zyklusStart = wochenMontag(p.zyklusStart);
  await db.trainingsplaene.update(id, { ...p, updatedAt: nowInstant() });
}

/** Diesen Plan (seine Familie) zum aktiven machen - „Heute“ und Kalender gehen danach. */
export async function planAktivieren(id: ID): Promise<void> {
  await db.transaction('rw', db.trainingsplaene, async () => {
    const plan = await db.trainingsplaene.get(id);
    if (!plan) return;
    const ts = nowInstant();
    for (const p of await db.trainingsplaene.toArray()) {
      const soll = p.familie === plan.familie && !p.gueltigBis;
      if (p.aktiv !== soll) await db.trainingsplaene.update(p.id, { aktiv: soll, updatedAt: ts });
    }
  });
}

async function einheitenKopieren(vonPlan: ID, nachPlan: ID): Promise<void> {
  const ts = nowInstant();
  const alte = await db.planEinheiten.where('planId').equals(vonPlan).toArray();
  await db.planEinheiten.bulkAdd(alte.map((e) => ({ ...structuredClone(e), id: newId(), planId: nachPlan, createdAt: ts, updatedAt: ts })));
}

/**
 * Ueberarbeiten: neue Version ab `gueltigAb`, die alte gilt bis dahin und
 * bleibt im Archiv - vergangene Tage behalten ihren Plan.
 */
export async function neueVersion(id: ID, gueltigAb: LocalDate): Promise<TrainingsPlan | null> {
  return db.transaction('rw', db.trainingsplaene, db.planEinheiten, async () => {
    const alt = await db.trainingsplaene.get(id);
    if (!alt || alt.gueltigBis) return null;
    const ts = nowInstant();
    const neu: TrainingsPlan = { ...structuredClone(alt), id: newId(), version: alt.version + 1, gueltigAb, createdAt: ts, updatedAt: ts };
    delete neu.gueltigBis;
    await db.trainingsplaene.add(neu);
    await db.trainingsplaene.update(id, { gueltigBis: gueltigAb, aktiv: false, updatedAt: ts });
    await einheitenKopieren(id, neu.id);
    return neu;
  });
}

/** Als eigenen Plan kopieren (z. B. Zuhause- aus dem Hauptplan). */
export async function planDuplizieren(id: ID, name: string): Promise<TrainingsPlan | null> {
  return db.transaction('rw', db.trainingsplaene, db.planEinheiten, async () => {
    const alt = await db.trainingsplaene.get(id);
    if (!alt) return null;
    const ts = nowInstant();
    const nid = newId();
    const neu: TrainingsPlan = { ...structuredClone(alt), id: nid, familie: nid, name: name.trim() || `${alt.name} (Kopie)`, version: 1, gueltigAb: today(), aktiv: false, createdAt: ts, updatedAt: ts };
    delete neu.gueltigBis;
    await db.trainingsplaene.add(neu);
    await einheitenKopieren(id, nid);
    return neu;
  });
}

/** Ganze Planfamilie mit allen Versionen und Einheiten loeschen - 30 Tage im Papierkorb (F-45). */
export async function planLoeschen(id: ID): Promise<void> {
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'trainingsplaene', zeileIds: [id] }, () => db.transaction('rw', db.trainingsplaene, db.planEinheiten, async () => {
    const plan = await db.trainingsplaene.get(id);
    if (!plan) return;
    const familie = await db.trainingsplaene.where('familie').equals(plan.familie).toArray();
    for (const p of familie) {
      await db.planEinheiten.where('planId').equals(p.id).delete();
      await db.trainingsplaene.delete(p.id);
    }
  }));
}

// --- Einheiten -----------------------------------------------------------------

async function amEnde(planId: ID, wochentag: Wochentag): Promise<number> {
  const alle = await db.planEinheiten.where('planId').equals(planId).filter((e) => e.wochentag === wochentag).toArray();
  return Math.max(0, ...alle.map((e) => e.order)) + 1;
}

async function tagAufTraining(planId: ID, wochentag: Wochentag): Promise<void> {
  const plan = await db.trainingsplaene.get(planId);
  if (plan && plan.tage[wochentag] === 'ruhe') await db.trainingsplaene.update(planId, { tage: { ...plan.tage, [wochentag]: 'training' }, updatedAt: nowInstant() });
}

export async function einheitAnlegen(planId: ID, wochentag: Wochentag, typ: EinheitTyp, name?: string, woche?: number): Promise<PlanEinheit> {
  return db.transaction('rw', db.trainingsplaene, db.planEinheiten, async () => {
    const ts = nowInstant();
    const e: PlanEinheit = {
      id: newId(), planId, wochentag, order: await amEnde(planId, wochentag), name: name?.trim() || NAMEN[typ], typ, uebungen: [],
      createdAt: ts, updatedAt: ts,
    };
    if (woche) e.woche = woche;
    if (typ === 'ausdauer') e.ausdauer = { intensitaet: 'locker' };
    if (typ === 'zirkel') e.zirkel = { runden: 3, arbeitSek: 60, pauseSek: 15, rundenPauseSek: 120 };
    await db.planEinheiten.add(e);
    await tagAufTraining(planId, wochentag);
    return e;
  });
}

const NAMEN: Record<EinheitTyp, string> = { kraft: 'Krafttraining', ausdauer: 'Lauf', zirkel: 'Zirkel', mobility: 'Mobility', skill: 'Skill-Training' };

export type EinheitsAenderung = Partial<Pick<PlanEinheit, 'name' | 'typ' | 'uhrzeit' | 'dauerMin' | 'aufwaermen' | 'aufwaermenMin' | 'ausdauer' | 'zirkel' | 'woche' | 'habitId'>>;

export async function einheitAendern(id: ID, patch: EinheitsAenderung): Promise<void> {
  const e = await db.planEinheiten.get(id);
  if (!e) return;
  const neu: PlanEinheit = { ...e, ...patch, updatedAt: nowInstant() };
  for (const k of ['uhrzeit', 'dauerMin', 'aufwaermen', 'aufwaermenMin', 'woche', 'habitId'] as const) {
    if (k in patch && (patch[k] === undefined || patch[k] === '' || patch[k] === null)) delete neu[k];
  }
  await db.planEinheiten.put(neu);
}

/** Per Ziehen: an einen anderen Tag bzw. zwischen zwei Nachbarn. */
export async function einheitEinsortieren(id: ID, wochentag: Wochentag, vorherId: ID | null, nachherId: ID | null): Promise<void> {
  await db.transaction('rw', db.trainingsplaene, db.planEinheiten, async () => {
    const [e, vorher, nachher] = await Promise.all([db.planEinheiten.get(id), vorherId ? db.planEinheiten.get(vorherId) : undefined, nachherId ? db.planEinheiten.get(nachherId) : undefined]);
    if (!e) return;
    await db.planEinheiten.update(id, { wochentag, order: orderZwischen(vorher?.order, nachher?.order), updatedAt: nowInstant() });
    await tagAufTraining(e.planId, wochentag);
  });
}

export async function einheitKopieren(id: ID, wochentag?: Wochentag): Promise<PlanEinheit | null> {
  return db.transaction('rw', db.trainingsplaene, db.planEinheiten, async () => {
    const e = await db.planEinheiten.get(id);
    if (!e) return null;
    const ts = nowInstant();
    const tag = wochentag ?? e.wochentag;
    const kopie: PlanEinheit = { ...structuredClone(e), id: newId(), wochentag: tag, name: wochentag ? e.name : `${e.name} (Kopie)`, order: await amEnde(e.planId, tag), createdAt: ts, updatedAt: ts };
    await db.planEinheiten.add(kopie);
    await tagAufTraining(e.planId, tag);
    return kopie;
  });
}

/** Alle Einheiten eines Tages auf einen anderen kopieren - samt Tagesart. */
export async function tagKopieren(planId: ID, von: Wochentag, nach: Wochentag, woche?: number): Promise<number> {
  return db.transaction('rw', db.trainingsplaene, db.planEinheiten, async () => {
    const ts = nowInstant();
    const quelle = await db.planEinheiten.where('planId').equals(planId).filter((e) => e.wochentag === von && (woche === undefined || e.woche === undefined || e.woche === woche)).toArray();
    let order = await amEnde(planId, nach);
    for (const e of quelle.sort((a, b) => a.order - b.order)) {
      await db.planEinheiten.add({ ...structuredClone(e), id: newId(), wochentag: nach, order: order++, createdAt: ts, updatedAt: ts });
    }
    const plan = await db.trainingsplaene.get(planId);
    if (plan) await db.trainingsplaene.update(planId, { tage: { ...plan.tage, [nach]: plan.tage[von] }, updatedAt: ts });
    return quelle.length;
  });
}

export const einheitLoeschen = (id: ID): Promise<void> =>
  inDenPapierkorb({ art: 'geloescht', tabelle: 'planEinheiten', zeileIds: [id] }, () => db.planEinheiten.delete(id));

// --- Uebungen einer Einheit ---------------------------------------------------

async function uebungenSetzen(einheitId: ID, f: (u: PlanUebung[]) => PlanUebung[]): Promise<void> {
  await db.transaction('rw', db.planEinheiten, async () => {
    const e = await db.planEinheiten.get(einheitId);
    if (e) await db.planEinheiten.update(einheitId, { uebungen: f(structuredClone(e.uebungen)), updatedAt: nowInstant() });
  });
}

export async function uebungEinplanen(einheitId: ID, uebungId: ID): Promise<void> {
  const u = await db.uebungen.get(uebungId);
  if (!u) return;
  const pu: PlanUebung = { id: newId(), uebungId, saetze: standardSaetze(u) };
  if (u.standard.pauseSek !== undefined) pu.pauseSek = u.standard.pauseSek;
  if (u.stufen?.length) pu.stufe = u.stufen[0];
  await uebungenSetzen(einheitId, (alle) => [...alle, pu]);
}

export const planUebungAendern = (einheitId: ID, puId: string, patch: Partial<Omit<PlanUebung, 'id'>>) =>
  uebungenSetzen(einheitId, (alle) => alle.map((pu) => {
    if (pu.id !== puId) return pu;
    const neu: PlanUebung = { ...pu, ...patch };
    for (const k of ['pauseSek', 'notiz', 'block', 'stufe'] as const) if (k in patch && (patch[k] === undefined || patch[k] === '')) delete neu[k];
    return neu;
  }));

export const planUebungEntfernen = (einheitId: ID, puId: string) => uebungenSetzen(einheitId, (alle) => alle.filter((pu) => pu.id !== puId));

/** An eine neue Stelle (Index nach dem Entfernen) verschieben. */
export const planUebungVerschieben = (einheitId: ID, puId: string, nachIndex: number) =>
  uebungenSetzen(einheitId, (alle) => {
    const i = alle.findIndex((pu) => pu.id === puId);
    if (i < 0) return alle;
    const [pu] = alle.splice(i, 1);
    alle.splice(Math.max(0, Math.min(alle.length, nachIndex)), 0, pu!);
    return alle;
  });

/** Bloecke mit nur einer Uebung sind keine - Kennung entfernen. */
function einzelBloeckeLoesen(alle: PlanUebung[]): PlanUebung[] {
  const anzahl = new Map<string, number>();
  for (const x of alle) if (x.block) anzahl.set(x.block, (anzahl.get(x.block) ?? 0) + 1);
  for (const x of alle) if (x.block && anzahl.get(x.block) === 1) delete x.block;
  return alle;
}

/** Mit der naechsten Uebung zu einem Supersatz verbinden bzw. die Verbindung loesen. */
export const supersatzUmschalten = (einheitId: ID, puId: string) =>
  uebungenSetzen(einheitId, (alle) => {
    const i = alle.findIndex((pu) => pu.id === puId);
    const a = alle[i];
    const b = alle[i + 1];
    if (!a || !b) return alle;
    if (a.block && a.block === b.block) {
      // Trennen: der Teil ab b bekommt eine eigene Kennung.
      const alt = a.block;
      const neu = newId();
      for (let j = i + 1; j < alle.length && alle[j]!.block === alt; j++) alle[j]!.block = neu;
      return einzelBloeckeLoesen(alle);
    }
    const ziel = a.block ?? newId();
    const bAlt = b.block;
    a.block = ziel;
    b.block = ziel;
    if (bAlt) for (let j = i + 2; j < alle.length && alle[j]!.block === bAlt; j++) alle[j]!.block = ziel;
    return einzelBloeckeLoesen(alle);
  });

// --- Import --------------------------------------------------------------------

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();

/** Uebernimmt eine geprueft Tabelle als neuen Plan. Unbekannte Uebungen werden angelegt (ohne Muskeln). */
export async function planAusImport(name: string, ergebnis: ImportErgebnis): Promise<{ plan: TrainingsPlan; neueUebungen: string[] }> {
  await bibliothekSicherstellen();
  const plan = await planAnlegen(name);
  const nachName = new Map((await db.uebungen.toArray()).map((u) => [norm(u.name), u]));
  const neueUebungen: string[] = [];
  const ts = nowInstant();
  const tage = { ...plan.tage };
  let order = 1;
  for (const ie of ergebnis.einheiten) {
    const uebungen: PlanUebung[] = [];
    for (const iu of ie.uebungen) {
      let u = nachName.get(norm(iu.name));
      if (!u) {
        u = await uebungAnlegen(iu.name, ie.typ === 'ausdauer' ? 'kraft' : ie.typ);
        nachName.set(norm(iu.name), u);
        neueUebungen.push(iu.name);
      }
      const pu: PlanUebung = { id: newId(), uebungId: u.id, saetze: iu.saetze };
      if (iu.pauseSek) pu.pauseSek = iu.pauseSek;
      if (iu.notiz) pu.notiz = iu.notiz;
      uebungen.push(pu);
    }
    const e: PlanEinheit = { id: newId(), planId: plan.id, wochentag: ie.wochentag, order: order++, name: ie.name, typ: ie.typ, uebungen, createdAt: ts, updatedAt: ts };
    if (ie.uhrzeit) e.uhrzeit = ie.uhrzeit;
    if (ie.woche) e.woche = ie.woche;
    if (ie.ausdauer) e.ausdauer = ie.ausdauer;
    await db.planEinheiten.add(e);
    tage[ie.wochentag] = 'training';
  }
  const wochen = Math.max(1, ...ergebnis.einheiten.map((e) => e.woche ?? 1));
  await db.trainingsplaene.update(plan.id, { tage, wochen, ...(wochen > 1 ? { zyklusStart: wochenMontag(plan.gueltigAb) } : {}), updatedAt: ts });
  return { plan: (await db.trainingsplaene.get(plan.id))!, neueUebungen };
}

// --- Einstellungen -------------------------------------------------------------

export async function einstellungenAendern(patch: Partial<Omit<TrainingsEinstellungen, 'id' | 'updatedAt'>>): Promise<void> {
  await db.transaction('rw', db.trainingsEinstellungen, async () => {
    const alt = (await db.trainingsEinstellungen.get('standard')) ?? { id: 'standard' as const, updatedAt: '' };
    await db.trainingsEinstellungen.put({ ...alt, ...patch, updatedAt: nowInstant() });
  });
}

// --- Koerpergewicht und Muskelgefuehl (C3) -------------------------------------

/** Ein Wert je Tag: ein neuer Eintrag am selben Tag ersetzt den alten. */
export async function gewichtEintragen(datum: LocalDate, kg: number): Promise<void> {
  if (!Number.isFinite(kg) || kg < 20 || kg > 400) return;
  await db.transaction('rw', db.koerpergewicht, async () => {
    await db.koerpergewicht.where('datum').equals(datum).delete();
    await db.koerpergewicht.add({ id: newId(), datum, kg: Math.round(kg * 10) / 10, createdAt: nowInstant() });
  });
}

export async function gewichtLoeschen(id: ID): Promise<void> {
  const g = await db.koerpergewicht.get(id);
  const titel = g ? `${g.kg.toLocaleString('de-DE')} kg am ${formatDate(g.datum)}` : undefined;
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'koerpergewicht', zeileIds: [id], titel }, () => db.koerpergewicht.delete(id));
}

/** „Muskelkater/fuehlt sich muede an“ fuer heute an- bzw. ausschalten. */
export async function muedeUmschalten(datum: LocalDate, muskel: MuskelId): Promise<void> {
  const id = `${datum}|${muskel}`;
  await db.transaction('rw', db.muskelgefuehl, async () => {
    if (await db.muskelgefuehl.get(id)) await db.muskelgefuehl.delete(id);
    else await db.muskelgefuehl.add({ id, datum, muskel });
  });
}
